import React from 'react';
import './CampaignIssueTable.css';

const text = value => typeof value === 'string' ? value : JSON.stringify(value);

const LABEL = { error: 'Error', warning: 'Warning', info: 'Info' };

// Errors, then warnings, then notes. A launch now reports three severities and
// the model returns them in whatever order it wrote them, so without this an
// unresolved geo-exclusion — the row that decides whether a campaign buys a
// place the brief said to avoid — can land underneath four notes about
// optional cells. Stable within each band: the source order inside a severity
// is the order the launcher produced, which is the order things happened in.
const RANK = { error: 0, warning: 1, info: 2 };
const bySeverity = rows => rows
  .map((row, index) => ({ row, index }))
  .sort((a, b) => (RANK[a.row.severity] ?? 1) - (RANK[b.row.severity] ?? 1) || a.index - b.index)
  .map(entry => entry.row);

function IssueTable({ rows, structured = false, label }) {
  if (!rows.length) return null;
  return (
    <div className="campaign-issues-scroll" role="region" aria-label={label} tabIndex={0}>
      <table className="campaign-issues-table">
        <caption>{label}</caption>
        <thead><tr>
          <th scope="col">Severity</th><th scope="col">{structured ? 'Issue' : 'Message'}</th>
          {structured && <><th scope="col">Affected items</th><th scope="col">Impact</th><th scope="col">Next step</th></>}
        </tr></thead>
        <tbody>{rows.map((row, index) => <tr key={index}>
          <td><span className={`campaign-issue-badge ${row.severity}`}>{LABEL[row.severity] || LABEL.warning}</span></td>
          <td>{row.issue}</td>
          {structured && <><td>{row.affected || 'Not specified'}</td><td>{row.impact || 'Not specified'}</td><td>{row.action || 'Not specified'}</td></>}
        </tr>)}</tbody>
      </table>
    </div>
  );
}

export default function CampaignIssueTable({ summary, errors = [], warnings = [], infos = [], hideHighOperationCountWarnings = false }) {
  const visible = value => !hideHighOperationCountWarnings || !/^High operation count:/i.test(String(value || '').trim());
  const rowsFromText = (errorItems, warningItems, infoItems) => [
    ...(errorItems || []).map(issue => ({ severity: 'error', issue: text(issue) })),
    ...(warningItems || []).filter(visible).map(issue => ({ severity: 'warning', issue: text(issue) })),
    ...(infoItems || []).map(issue => ({ severity: 'info', issue: text(issue) }))
  ];
  const rawRows = rowsFromText(errors, warnings, infos);
  const structured = Array.isArray(summary?.issues) && summary.issues.length > 0;
  const summaryRows = structured
    ? summary.issues.filter(row => row.severity !== 'warning' || visible(row.issue))
    : rowsFromText(summary?.errors, summary?.warnings, summary?.infos);
  const rows = bySeverity(summaryRows.length ? summaryRows : rawRows);
  if (!rows.length && !summary?.loading && !summary?.error) return null;
  const count = severity => rows.filter(row => row.severity === severity).length;
  const infoCount = count('info');
  return <section className="campaign-issues" aria-label="Campaign issues">
    <div className="campaign-issues-heading">
      <strong>{summaryRows.length ? 'What happened' : 'Errors and warnings'}</strong>
      <span>
        <span>Errors ({count('error')})</span> · <span>Warnings ({rows.length - count('error') - infoCount})</span>
        {infoCount > 0 && <> · <span>Info ({infoCount})</span></>}
      </span>
    </div>
    {summary?.loading && <p className="campaign-issues-status" role="status">Generating summary...</p>}
    {summary?.error && <p className="campaign-issues-status" role="status">Summary unavailable. Review the source messages or try again.</p>}
    <IssueTable rows={rows} structured={structured && summaryRows.length > 0} label={summaryRows.length ? 'Issue summary' : 'Source messages'} />
    {summaryRows.length > 0 && rawRows.length > 0 && <details className="campaign-issues-source">
      <summary>Source messages ({rawRows.length})</summary>
      <IssueTable rows={bySeverity(rawRows)} label="Exact source messages" />
    </details>}
  </section>;
}
